import React, { useMemo, useRef } from "react";
import { useParams } from "react-router";
import { useGetProductsInfiniteQuery } from "../../../providers/products";
import { useActiveOrMainShippingZone } from "../../../providers/shippingZones";
import Page from "../../page-layout";
import MaxWidthContainer from "../../../components/UI/MaxWidthContainer/MaxWidthContainer";
import {
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonSkeletonText,
} from "@ionic/react";
import { BackButton, NewListViewProduct } from "@core/components";
import { useGetCategoryByIdQuery, useGetSubCategoriesQuery } from "../../../providers/categories";
import cx from "classnames";
import { CategoryCard } from "../../../parhato2/src/entities/Category/CategoryComponent/Category";
import SearchButton from "../../../parhato2/src/entities/ui/search-button";
import CategoryFilters from "../../../parhato2/src/entities/CategoryFilters";
import { useSearchParams } from "../../../hooks/useSearchParams";
import SubcategoriesSkeleton from "./subcategories-skeleton";
import NoProductsFound from "../../../parhato2/src/entities/ui/no-products-found";
import { AdditionalFeature, Product } from "@core/types";
import PullToRefresh from "../../../parhato2/src/entities/pullToRefresh";
import { useTranslation } from "react-i18next";
interface RouteParams {
  id: string;
}

const SubCategoriesPage = () => {
  const { t } = useTranslation();
  const params = useParams<RouteParams>();
  const categoryId = params.id;
  const { get } = useSearchParams();
  const filterValue = get("additionalProductFilter");
  const currentFilter = filterValue === null ? undefined : filterValue;
  const infiniteRef = useRef<HTMLIonInfiniteScrollElement | null>(null);

  const getSubCategoriesEnabled = !!categoryId;

  const { data: category, isLoading: isCategoryLoading } = useGetCategoryByIdQuery(params.id!, {
    enabled: getSubCategoriesEnabled,
  });

  const { data: subCategories, isLoading: isSubCategoriesLoading } = useGetSubCategoriesQuery(params.id!, {
    enabled: getSubCategoriesEnabled,
  });

  const { activeShippingZone, isLoading: isActiveShippingZoneLoading } = useActiveOrMainShippingZone();

  const productsFromParentAndChildCategoriesEnabled = !!categoryId && !!activeShippingZone.id;

  const {
    data: productResults,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isProductResultsLoading,
    isFetching: isProductsFetching,
  } = useGetProductsInfiniteQuery({
    options: {
      enabled: productsFromParentAndChildCategoriesEnabled,
    },
    params: {
      pageNumber: "1",
      pageSize: "12",
      categoryId,
      additionalProductFilter: currentFilter,
      zoneId: activeShippingZone.id,
    },
  });

  const handleShowMoreClick = () => {
    if (hasNextPage) {
      fetchNextPage();
    }
  };

  const productsList = useMemo(() => {
    const productsMap = new Map<string | number, Product>();
    productResults?.pages.forEach((page) => {
      page?.products?.forEach((product) => {
        if (product && !productsMap.has(product.id)) {
          productsMap.set(product.id, product);
        }
      });
    });
    return Array.from(productsMap.values());
  }, [productResults]);

  const additionalFilters = useMemo(() => {
    const productsMap = new Map<string | number, AdditionalFeature>();
    productResults?.pages.forEach((page) => {
      page?.additionalFeatures?.forEach((additionalFeature) => {
        if (additionalFeature && !productsMap.has(additionalFeature.additionalFeature)) {
          productsMap.set(additionalFeature.additionalFeature, additionalFeature);
        }
      });
    });
    return Array.from(productsMap.values());
  }, [productResults]);

  const hasProducts = !!productsList?.length;
  const isInfiniteScrollDisabled = !hasNextPage && !isFetchingNextPage;

  const isLoading =
    ((isCategoryLoading || isSubCategoriesLoading) && getSubCategoriesEnabled) ||
    isActiveShippingZoneLoading ||
    (productsFromParentAndChildCategoriesEnabled && isProductResultsLoading);

  const FilterSkeleton = () => {
    return (
      <div className="flex gap-2.5 ml-5 w-full h-8">
        <IonSkeletonText className="w-[7.1875rem] h-8 rounded-2xl" animated />
        <IonSkeletonText className="w-[8.1875rem] h-8 rounded-2xl" animated />
        <IonSkeletonText className="w-[5.42rem] h-8 rounded-2xl" animated />
        <IonSkeletonText className="w-[8rem] h-8 rounded-2xl" animated />
        <IonSkeletonText className="w-[3rem] h-8 rounded-2xl" animated />
      </div>
    );
  };

  return (
    <Page>
      <Page.Header className="pt-[env(safe-area-inset-top)] ">
        <MaxWidthContainer classList="flex items-center justify-center px-5 py-5 h-16 bg-white">
          <div className="w-full flex items-center ">
            <BackButton />
            <div className="flex-1 text-center text-[1.125rem] font-bold truncate mx-1">{category?.name}</div>
            <SearchButton />
          </div>
        </MaxWidthContainer>
      </Page.Header>
      <Page.Content>
        <PullToRefresh />
        <MaxWidthContainer>
          {isLoading ? (
            <SubcategoriesSkeleton />
          ) : (
            <>
              <div className="relative rounded-t-3xl min-h-screen bg-white">
                <div className={cx("flex flex-col gap-[1.875rem]", !hasProducts && "gap-12")}>
                  <div className="flex flex-wrap justify-center items-center gap-1.5">
                    {subCategories?.map((cat) => {
                      return <CategoryCard key={cat.id} category={cat} />;
                    })}
                  </div>

                  <div className="flex flex-col gap-5 pt-5">
                    {isProductResultsLoading ? (
                      <FilterSkeleton />
                    ) : additionalFilters && additionalFilters.length > 0 ? (
                      <>
                        <CategoryFilters categoryId={categoryId} categoriesFilters={additionalFilters} />
                        {isProductsFetching && !isProductResultsLoading && !isFetchingNextPage && (
                          <div className="flex flex-col gap-2 p-5 bg-white">
                            <div className="grid grid-cols-2 xs:grid-cols-3 md:grid-cols-4 gap-y-2.5 gap-x-2.5">
                              {[...Array(6)].map((_, index) => (
                                <IonSkeletonText key={index} className="w-full h-[12rem] rounded-2xl" animated />
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    ) : null}

                    {!!productsList?.length ? (
                      <div className="flex flex-col gap-2 p-5">
                        <div className="grid grid-cols-2 xs:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-y-2.5 gap-x-2.5">
                          {productsList?.map((product) => (
                            <NewListViewProduct
                              key={product.id}
                              product={product}
                              type="newList"
                              lazyload={false}
                              className=""
                            />
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="flex justify-center items-center h-full">
                        <NoProductsFound />
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div
                className="flex flex-col items-center justify-center gap-2 mt-5"
                style={{ visibility: hasNextPage ? "visible" : "hidden" }}
              >
                <button
                  disabled={isFetchingNextPage}
                  onClick={handleShowMoreClick}
                  className="flex items-center px-14 py-3 rounded-xl bg-primary"
                >
                  {t("Show more")}
                </button>
              </div>
              <IonInfiniteScroll
                className="flex items-center justify-center mt-4"
                disabled={isInfiniteScrollDisabled}
                threshold="100px"
                ref={infiniteRef}
                onIonInfinite={async () => {
                  if (hasNextPage) {
                    await fetchNextPage();
                  }
                  requestAnimationFrame(() => {
                    infiniteRef.current?.complete();
                  });
                }}
              >
                <IonInfiniteScrollContent loadingSpinner="lines" />
              </IonInfiniteScroll>
            </>
          )}
        </MaxWidthContainer>
      </Page.Content>
    </Page>
  );
};

export default SubCategoriesPage;
